import { useFormikContext } from "formik";
import React from "react";
import ProductType from "../../../../common/interfaces/enums/ProductType";
import { IQuoteSettings } from "../../../interfaces/form/IQuoteSettings";
import Input from "../../../../common/components/Forms/Input/Input";
import Dropdown from "../../../../common/components/Forms/Dropdown/Dropdown";
import { PaymentFrequency } from "../../../../common/interfaces/enums/PaymentFrequency";
import useSettingsValidation from "../../../hooks/useSettingsValidation";
import { calculateMortgageMonthlyRepayments } from "../../../../common/shared/productHelpers";

const repaymentFrequencyOptions = [
    { value: PaymentFrequency.Weekly, label: "Weekly" },
    { value: PaymentFrequency.Fortnightly, label: "Fortnightly" },
    { value: PaymentFrequency.Monthly, label: "Monthly" }
];

const GlobalProductFormMortgage: React.FC = () => {
    const { values, handleBlur, handleChange, setFieldValue } = useFormikContext<
        IQuoteSettings
    >();

    const { getFormikError } = useSettingsValidation();

    const updateMonthlyRepayment = (
        amount: number,
        frequency: PaymentFrequency
    ): void => {
        let monthlyCover = calculateMortgageMonthlyRepayments(amount, frequency);

        // It can be funky at small values, so if it's low we just don't auto fill.
        if (monthlyCover < 100) {
            monthlyCover = undefined;
        }

        values.clients.forEach((client, index) => {
            // Only update clients that have a mortgage setting
            if (client.mortgage) {
                setFieldValue(
                    `clients.${index}.${ProductType.Mortgage}.monthlyCover`,
                    monthlyCover
                );
            }
        });
    };

    return (
        <React.Fragment>
            <Input
                id={`${ProductType.Mortgage}.mortgageAmount`}
                name={`${ProductType.Mortgage}.mortgageAmount`}
                label={`Mortgage Amount`}
                type="number"
                hasCurrency
                value={values[ProductType.Mortgage]?.mortgageAmount}
                onBlur={handleBlur}
                onChange={(e) => {
                    setFieldValue(`${ProductType.Mortgage}.mortgageAmount`, e, true);
                }}
                hasError={getFormikError(`${ProductType.Mortgage}.mortgageAmount`)}
            />
            {/* Repayment amount & frequency inputs display inline here */}
            <div className="gap-m layout-flex-column flex-gap-m">
                <Input
                    id={`${ProductType.Mortgage}.repaymentAmount`}
                    name={`${ProductType.Mortgage}.repaymentAmount`}
                    type={`number`}
                    label={"Mortgage Repayments"}
                    hasCurrency
                    value={values[ProductType.Mortgage]?.repaymentAmount}
                    onBlur={handleBlur}
                    onChange={async (e) => {
                        await setFieldValue(
                            `${ProductType.Mortgage}.repaymentAmount`,
                            e,
                            true
                        );

                        updateMonthlyRepayment(
                            e,
                            values[ProductType.Mortgage]?.repaymentFrequency
                        );
                    }}
                    hasError={getFormikError(`${ProductType.Mortgage}.repaymentAmount`)}
                />
                <Dropdown
                    id={`${ProductType.Mortgage}.repaymentFrequency`}
                    name={`${ProductType.Mortgage}.repaymentFrequency`}
                    noMargin
                    selectedOptionId={values[
                        ProductType.Mortgage
                    ]?.repaymentFrequency.toString()}
                    options={repaymentFrequencyOptions.map((rf) => ({
                        ...rf,
                        selected:
                            rf.value === values[ProductType.Mortgage]?.repaymentFrequency
                    }))}
                    onBlur={handleBlur}
                    onChange={(e) => {
                        const freq = e.target.value as PaymentFrequency;
                        updateMonthlyRepayment(values.mortgage.repaymentAmount, freq);

                        handleChange(e);
                    }}
                    hasError={getFormikError(
                        `${ProductType.Mortgage}.repaymentFrequency`
                    )}
                />
            </div>
        </React.Fragment>
    );
};

export default GlobalProductFormMortgage;
